import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useWebhookTrigger } from '@/modules/app/hooks/useWebhookTrigger';
import { useWorkspaceCreationForSearch } from '@/modules/app/hooks/useAutomaticWorkspaceCreation';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthGuard } from '@/components/AuthGuard';
import { useAuth } from '@/contexts/AuthContext';
import { debugLog, isDebugMode } from '@/lib/debugUtils';

// Import new container components
import SearchForm from '@/modules/app/components/keyword-research/containers/SearchForm';
import ResultsDisplay from '@/modules/app/components/keyword-research/containers/ResultsDisplay';

// Define search params type
interface SearchParams {
  query: string;
  langName: string;
  countryName: string;
}

const AppKeywordResearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useState<SearchParams | null>(null);
  
  const { triggerWebhook, isLoading: isWebhookLoading, error: webhookError } = useWebhookTrigger();
  const { 
    createWorkspaceForSearch, 
    workspaceState, 
    resetWorkspaceState,
    isCreatingWorkspace 
  } = useWorkspaceCreationForSearch();
  
  // Get authenticated user ID from AuthContext
  const { userId, isAuthenticated, isLoading: isAuthLoading, user } = useAuth();

  // Debug logging for authorized users only
  useEffect(() => {
    const userEmail = user?.primaryEmailAddress?.emailAddress;
    if (isDebugMode(userEmail)) {
      debugLog(userEmail, 'Rendering AppKeywordResearchPage');
      if (searchParams) {
        debugLog(userEmail, 'Current state:', {
          searchParams,
          currentQuery: searchParams.query,
          currentLanguageName: searchParams.langName, 
          currentCountryName: searchParams.countryName,
          userId,
          isAuthenticated,
          workspaceState
        });
      }
    }
  }, [searchParams, userId, isAuthenticated, workspaceState, user]);

  /**
   * Simplified handleSearch function using the new workspace creation hook
   */
  const handleSearch = useCallback(async (keyword: string, languageName: string, countryName: string) => { 
    const userEmail = user?.primaryEmailAddress?.emailAddress;
    debugLog(userEmail, "CONFIRMATION LOG: Executing AppKeywordResearchPage.handleSearch - REFACTORED VERSION");
    
    if (!isAuthenticated || !userId) {
      debugLog(userEmail, '[AppKeywordResearchPage] User not authenticated or userId missing');
      return;
    }
    
    if (!keyword?.trim()) {
      debugLog(userEmail, 'Empty keyword, returning early');
      return;
    }
    
    try {
      // Reset workspace state
      resetWorkspaceState();
      
      // Phase 1: Create workspace automatically using the new hook
      debugLog(userEmail, '[AppKeywordResearchPage] Phase 1: Creating workspace');
      const { workspaceId, workspaceName: finalWorkspaceName } = await createWorkspaceForSearch(
        keyword, 
        languageName, 
        countryName
      );
      
      // Phase 2: Start keyword research
      debugLog(userEmail, '[AppKeywordResearchPage] Phase 2: Starting keyword search');
      
      if (isDebugMode(userEmail)) {
        debugLog(userEmail, 'Calling triggerWebhook with workspace support:', { 
          keyword, 
          languageName, 
          countryName, 
          userId,
          workspaceId,
          workspaceName: finalWorkspaceName
        });
      }
      
      // Send to webhook with workspace context
      const webhookResponse = await triggerWebhook({
        query: keyword,
        language_name: languageName, 
        location_name: countryName,
        workspace_id: workspaceId || undefined,
        workspace_name: finalWorkspaceName
      });

      debugLog(userEmail, '[AppKeywordResearchPage] Webhook response:', webhookResponse);
      
      // Update search params after successful webhook trigger
      setSearchParams(prevParams => {
        if (prevParams?.query === keyword && 
            prevParams.langName === languageName &&
            prevParams.countryName === countryName) {
          return prevParams;
        }
        
        return { 
          query: keyword, 
          langName: languageName,
          countryName: countryName
        };
      });
      
    } catch (error) {
      console.error('Error in handleSearch:', error); // Keep actual errors for all users
      resetWorkspaceState();
    }
  }, [
    triggerWebhook, 
    createWorkspaceForSearch,
    resetWorkspaceState,
    isAuthenticated, 
    userId,
    user
  ]);

  /**
   * Handle new research - opens fresh keyword research page in new tab
   */
  const handleNewResearch = useCallback(() => {
    const userEmail = user?.primaryEmailAddress?.emailAddress;
    debugLog(userEmail, "[AppKeywordResearchPage] Opening new research tab");
    
    // Open fresh keyword research page in new tab
    window.open('/app/keyword-research', '_blank');
  }, [user]);

  // Memoize derived values to prevent recreation on every render
  const currentQuery = useMemo(() => searchParams?.query || "", [searchParams?.query]);
  const currentLanguageName = useMemo(() => searchParams?.langName || "", [searchParams?.langName]);
  const currentCountryName = useMemo(() => searchParams?.countryName || "", [searchParams?.countryName]);
  
  // Simple loading message with workspace context
  const loadingMessage = useMemo(() => {
    if (isAuthLoading) return "Authenticating...";
    
    if (isCreatingWorkspace) {
      return "Creating workspace...";
    }
    
    if (isWebhookLoading) {
      return "Starting keyword research...";
    }
    
    if (searchParams?.query) {
      return `Processing search for "${searchParams.query}"...`;
    }
    
    return "Loading...";
  }, [isAuthLoading, isCreatingWorkspace, isWebhookLoading, searchParams?.query]);

  // Simple loading state with workspace operations
  const isLoading = useMemo(() => 
    isWebhookLoading || 
    isAuthLoading ||
    isCreatingWorkspace,
    [isWebhookLoading, isAuthLoading, isCreatingWorkspace]
  );

  debugLog(user?.primaryEmailAddress?.emailAddress, '[AppKeywordResearchPage] Rendering with userId:', userId);
  
  return (
    <AuthGuard>
      <div className="w-full min-h-screen">
        <ErrorBoundary>
          {/* Search Form */}
          <SearchForm
            onSearch={handleSearch}
            isLoading={isLoading}
            loadingMessage={loadingMessage}
            searchParams={searchParams}
            onNewResearch={handleNewResearch}
            hasResults={!!searchParams?.query}
          />
          
          {/* Results Display */}
          <ResultsDisplay
            currentQuery={currentQuery}
            currentLanguageName={currentLanguageName}
            currentCountryName={currentCountryName}
            userId={userId}
            workspaceId={workspaceState.workspaceCreated?.id}
            workspaceName={workspaceState.workspaceCreated?.name}
            strictWorkspaceFiltering={true} // Changed from false to true
          />
        </ErrorBoundary>
      </div>
    </AuthGuard>
  );
};

// Custom comparison function for React.memo
const arePropsEqual = (prevProps: any, nextProps: any) => {
  // Since this component doesn't receive props, it should only re-render
  // when its internal state or context values change
  return true;
};

export default React.memo(AppKeywordResearchPage, arePropsEqual);